import type { SnapshotLicenseUsage } from '@ipfabric/api-client/client/schemas';

export type LicenseUsageValues = {
  total: number | null;
  licensed: number | null;
};

export type SnapshotLicenseUsageValues = {
  onPremise: LicenseUsageValues;
  cloud: LicenseUsageValues;
};

export const toSnapshotLicenseUsageValues = (
  licenseUsage: NonNullable<SnapshotLicenseUsage>,
): SnapshotLicenseUsageValues => ({
  onPremise: toLicenseUsageValues(
    licenseUsage.onPremise.totalDevCount,
    licenseUsage.onPremise.licensedDevCount,
  ),
  cloud: toLicenseUsageValues(
    licenseUsage.cloud.totalCcuCount,
    licenseUsage.cloud.licensedCcuCount,
  ),
});

export const formatSnapshotLicenseUsage = ({
  onPremise,
  cloud,
}: SnapshotLicenseUsageValues): string =>
  [formatLicenseUsage(onPremise, 'On-prem'), formatLicenseUsage(cloud, 'Cloud')]
    .filter(Boolean)
    .join(', ');

export const toLicenseUsageValues = (
  total: number | string | null | undefined,
  licensed: number | string | null | undefined,
): LicenseUsageValues => ({
  total: toFiniteOrNull(total),
  licensed: toFiniteOrNull(licensed),
});

export const formatLicenseUsage = (
  { total, licensed }: LicenseUsageValues,
  label: string,
): string | null => {
  if (total === null) {
    if (licensed === null) {
      return null;
    }

    return `${label}: ${licensed.toLocaleString()}`;
  }

  if (licensed === null || licensed === total) {
    return `${label}: ${total.toLocaleString()}`;
  }

  return `${label}: ${total.toLocaleString()}/${licensed.toLocaleString()}`;
};

export const formatLicenseUsageDetail = (
  { total, licensed }: LicenseUsageValues,
  unit: string,
): string | null => {
  if (total === null) {
    if (licensed === null) {
      return null;
    }

    return `${licensed.toLocaleString()} ${unit}`;
  }

  if (licensed === null || licensed === total) {
    return `${total.toLocaleString()} ${unit}`;
  }

  return `${licensed.toLocaleString()} licensed of ${total.toLocaleString()} total ${unit}`;
};

const toFiniteOrNull = (
  value: number | string | null | undefined,
): number | null => {
  if (value === null || value === undefined) {
    return null;
  }
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};
